import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDashboard } from '../context/DashboardContext';
import { PROFILE_VIEW_MODE_LABELS } from '../types/profiles';
import { buildAnonymousLabels, indexOperatorsByRoomId, matchesViewMode } from '../utils/profileFilter';

const MODE_OPTIONS = [
  { value: 'todos', label: PROFILE_VIEW_MODE_LABELS.todos },
  { value: 'managed', label: PROFILE_VIEW_MODE_LABELS.managed },
  { value: 'professor', label: PROFILE_VIEW_MODE_LABELS.professor },
  { value: 'pai_mae', label: PROFILE_VIEW_MODE_LABELS.pai_mae },
] as const;

const SEX_OPTIONS = [
  { value: 'masculino', label: 'Masculino' },
  { value: 'feminino', label: 'Feminino' },
] as const;

export function ProfileFilter() {
  const {
    realProfiles,
    operators,
    profilesLoading,
    profileViewMode,
    setProfileViewMode,
    individualProfileId,
    setIndividualProfileId,
    ageRange,
    setAgeRange,
    sexFilter,
    setSexFilter,
  } = useDashboard();

  const operatorsByRoomId = useMemo(() => indexOperatorsByRoomId(operators), [operators]);
  const anonymousLabels = useMemo(() => buildAnonymousLabels(realProfiles), [realProfiles]);
  const individualProfiles = useMemo(
    () => realProfiles.filter((p) => matchesViewMode(p, profileViewMode, operatorsByRoomId)),
    [realProfiles, profileViewMode, operatorsByRoomId]
  );

  return (
    <div className="flex flex-wrap gap-4 mb-6 items-end">
      <div className="min-w-[220px]">
        <label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Visão</label>
        <Select
          value={profileViewMode}
          onValueChange={(v) => {
            setProfileViewMode(v as typeof profileViewMode);
            if (v === 'managed' || v === 'todos') setIndividualProfileId(undefined);
          }}
          disabled={profilesLoading}
        >
          <SelectTrigger className="h-9 rounded-full text-[13px] bg-background border-border px-3.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-sm">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="min-w-[140px]">
        <label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Gênero</label>
        <Select
          value={sexFilter ?? 'todos'}
          onValueChange={(v) => setSexFilter(v === 'todos' ? undefined : v)}
          disabled={profilesLoading}
        >
          <SelectTrigger className="h-9 rounded-full text-[13px] bg-background border-border px-3.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos" className="text-sm">
              Todos
            </SelectItem>
            {SEX_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-sm">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(profileViewMode === 'managed' || profileViewMode === 'todos') && (
        <>
          <div className="min-w-[80px]">
            <Label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Idade mín.</Label>
            <Input
              type="number"
              min={0}
              max={120}
              placeholder="—"
              value={ageRange.min ?? ''}
              onChange={(e) =>
                setAgeRange({ ...ageRange, min: e.target.value ? Number(e.target.value) : undefined })
              }
              className="h-9 rounded-full text-[13px] w-20 px-3.5"
            />
          </div>
          <div className="min-w-[80px]">
            <Label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Idade máx.</Label>
            <Input
              type="number"
              min={0}
              max={120}
              placeholder="—"
              value={ageRange.max ?? ''}
              onChange={(e) =>
                setAgeRange({ ...ageRange, max: e.target.value ? Number(e.target.value) : undefined })
              }
              className="h-9 rounded-full text-[13px] w-20 px-3.5"
            />
          </div>
        </>
      )}

      {(profileViewMode === 'professor' || profileViewMode === 'pai_mae') && (
        <div className="min-w-[180px]">
          <label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Participante</label>
          <Select
            value={individualProfileId ?? ''}
            onValueChange={(v) => setIndividualProfileId(v)}
            disabled={profilesLoading || individualProfiles.length === 0}
          >
            <SelectTrigger className="h-9 rounded-full text-[13px] bg-background border-border px-3.5">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {individualProfiles.map((p) => (
                <SelectItem key={p.id} value={p.id} className="text-sm">
                  {anonymousLabels.get(p.id)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {profilesLoading && (
        <span className="text-xs text-muted-foreground self-center">Sincronizando com o Firestore…</span>
      )}
    </div>
  );
}
